import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';
import qs from 'qs';
import { useHistory, useParams } from 'react-router-dom';
import { Link as RouterLink } from '~/lib/react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridFilterModel,
  GridRowId,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  getGridNumericOperators,
  getGridStringOperators,
} from '@mui/x-data-grid-pro';
import { GridFilterItem } from '@mui/x-data-grid/models/gridFilterItem';

import GridPagination from '~/components/Common/Elements/GridPagination';
import AdGroupTags from '~/components/GoogleAds/AdGroupTagsSelect';
import { BidAutomationRules } from '~/components/GoogleAds/BidAutomationRules';
import ManageTagsDialog from '~/components/GoogleAds/ManageTagsDialog';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import useQuery from '~/hooks/useQuery';

import {
  AdGroupListItem,
  GoogleAdsCampaignConfigItem,
  Tag,
  getAdGroupsByCampaign,
  listGoogleAdsCampaignsConfig,
  removeTagForAdGroups,
  searchTags,
  updateAdGroupTags,
} from '~/services/PublicOfferFeedService';

type AdGroupListItemWithTags = AdGroupListItem & {
  tags: Array<string | Tag>;
};

const gridNumericOperators = getGridNumericOperators().filter(
  (op) => op.value !== 'isEmpty' && op.value !== 'isNotEmpty',
);

const gridStringOperators = getGridStringOperators().filter((op) => op.value === 'contains');

function AdGroupListView() {
  const query = useQuery();
  const history = useHistory();
  const { customerId, campaignId } = useParams<{ customerId: string; campaignId: string }>();
  const [adGroups, setAdGroups] = useState<Array<AdGroupListItemWithTags>>([]);
  const [search, setSearch] = useState(query.get('search') || '');
  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZES[0]);
  const [total, setTotal] = useState(0);
  const [sortModel, setSortModel] = useState<{ field: string; sort: string }>({ field: 'ad_group_name', sort: 'asc' });
  const [selectionModel, setSelectionModel] = useState<Array<GridRowId>>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManageTagsModalOpen, setIsManageTagsModalOpen] = useState(false);
  const [availableTags, setAvailableTags] = useState<Array<Tag>>([]);
  const [campaignConfig, setCampaignConfig] = useState<GoogleAdsCampaignConfigItem | null>(null);

  // Initialize filter model from URL parameters
  const initialFilterItems: Array<GridFilterItem> = [];

  // Parse all filter parameters from URL
  const filterParams = query.getAll('filter');
  filterParams.forEach((filterParam, index) => {
    try {
      const filter = JSON.parse(filterParam);
      if (filter.field && filter.operator && filter.value !== undefined) {
        initialFilterItems.push({
          id: index + 1,
          field: filter.field,
          operator: filter.operator,
          value: filter.value,
        });
      }
    } catch (error) {
      console.warn('Invalid filter parameter:', filterParam);
    }
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: initialFilterItems,
  });
  const [debouncedFilterItems, setDebouncedFilterItems] = useState<Array<GridFilterItem>>(initialFilterItems);

  const handleDeleteTag = async (tag: Tag, adGroupId: string) => {
    try {
      const tagId = typeof tag === 'string' ? null : tag.tagId;
      if (!tagId) {
        console.error('Tag ID not found');
        return;
      }
      await removeTagForAdGroups(customerId, campaignId, tagId.toString(), [adGroupId]);
      fetchTags();
      fetchAdGroups(pageSize, pageNum, search, sortModel, filterModel.items);
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  const columns: Array<GridColDef> = [
    {
      field: 'ad_group_name',
      headerName: 'Ad Group',
      flex: 2,
      disableColumnMenu: true,
      filterable: false,
      renderCell: (params) => (
        <Link
          component={RouterLink}
          to={`/marketing/google-ads/customer/${customerId}/campaign/${campaignId}/ad-group/${params.row.ad_group_id}`}
        >
          {params.row.ad_group_name} ({params.row.ad_group_id})
        </Link>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 2,
      editable: true,
      minWidth: 200,
      sortable: false,
      filterOperators: gridStringOperators,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {params.row.tags.map((tag: Tag) => (
              <Chip
                key={typeof tag === 'string' ? tag : tag.tagId}
                label={typeof tag === 'string' ? tag : tag.tagName}
                size="small"
                onDelete={() => handleDeleteTag(tag, params.row.ad_group_id)}
                deleteIcon={<CloseIcon />}
              />
            ))}
          </Box>
        );
      },
      renderEditCell: (params) => (
        <AdGroupTags
          customerId={customerId}
          campaignId={campaignId}
          adGroupId={params.row.ad_group_id}
          selectedTags={(params.row.tags || []).map((tag: Tag | string) =>
            typeof tag === 'string' ? tag : tag.tagName,
          )}
          onTagsChange={(tags) => {
            params.api.setEditCellValue({ id: params.id, field: params.field, value: tags });
            params.api.stopCellEditMode({ id: params.id, field: params.field });
          }}
          availableTags={availableTags}
          onTagsRefresh={() => {
            fetchTags();
            fetchAdGroups(pageSize, pageNum, search, sortModel, filterModel.items);
          }}
        />
      ),
    },
    {
      field: 'hotel',
      headerName: 'Property Name',
      flex: 2,
      disableColumnMenu: true,
      filterable: false,
    },
    {
      field: 'spend',
      headerName: 'Spend',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.spend).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'margin',
      headerName: 'Margin',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.margin).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'mpv',
      headerName: 'Margin Per View',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.mpv).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'views',
      headerName: 'Views',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.views),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'clicks',
      headerName: 'Clicks',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.clicks),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.impressions),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'ctr',
      headerName: 'Click Through Rate',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.ctr).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'cpc',
      headerName: 'Cost Per Click',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.cpc).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'impression_share',
      headerName: 'Impression Share',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.impression_share).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'mroas',
      headerName: 'Margin Roas',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.mroas).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'conversion_value',
      headerName: 'Conversion Value',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.conversion_value).toFixed(2),
      filterOperators: gridNumericOperators,
    },
    {
      field: 'conversions',
      headerName: 'Conversions',
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => Number(params.row.conversions),
      filterOperators: gridNumericOperators,
    },
    ...(campaignConfig?.campaign_type === 'search'
      ? [
          {
            field: 'margin_per_view_change_3d_vs_7d_pct',
            headerName: 'Percentage of Margin per view change from 3 days to 7 days',
            flex: 1,
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderCell: (params) => Number(params.row.margin_per_view_change_3d_vs_7d_pct),
            filterOperators: gridNumericOperators,
          },
          {
            field: 'margin_per_view_change_1d_vs_7d_pct',
            headerName: 'Percentage of Margin per view change from 1 days to 7 days',
            flex: 1,
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderCell: (params) => Number(params.row.margin_per_view_change_1d_vs_7d_pct),
            filterOperators: gridNumericOperators,
          },
          {
            field: 'conversion_rate_change_3d_vs_7d_pct',
            headerName: 'Percentage of Conversion rate change from 3 days to 7 days',
            flex: 1,
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderCell: (params) => Number(params.row.conversion_rate_change_3d_vs_7d_pct),
            filterOperators: gridNumericOperators,
          },
          {
            field: 'conversion_rate_change_1d_vs_7d_pct',
            headerName: 'Percentage of Conversion rate change from 1 days to 7 days',
            flex: 1,
            align: 'right' as const,
            headerAlign: 'right' as const,
            renderCell: (params) => Number(params.row.conversion_rate_change_1d_vs_7d_pct),
            filterOperators: gridNumericOperators,
          },
        ]
      : []),
  ];

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm: string) => {
        setSearch(searchTerm);
      }, 500),
    [],
  );

  const debouncedUpdateFilterItems = useMemo(
    () =>
      debounce((items: Array<GridFilterItem>) => {
        setDebouncedFilterItems(items);
      }, 500),
    [],
  );

  useEffect(() => {
    setQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, debouncedFilterItems]);

  const setQueryParams = useCallback(() => {
    const searchParams: { search: string; filter?: Array<string> } = {
      search,
    };

    // Add all filter parameters to URL
    if (debouncedFilterItems.length > 0) {
      searchParams.filter = debouncedFilterItems.map((item) =>
        JSON.stringify({
          field: item.field,
          operator: item.operator,
          value: item.value,
        }),
      );
    }

    const queryString = qs.stringify(searchParams, { arrayFormat: 'repeat' });
    history.push({
      search: `?${queryString}`,
    });
  }, [history, search, debouncedFilterItems]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const fetchAdGroups = useCallback(
    async (
      size: number,
      page: number,
      searchTerm?: string,
      sortModel?: { field: string; sort: string },
      filterItems?: Array<GridFilterItem>,
    ) => {
      try {
        setFetchingState('loading');
        const response = await getAdGroupsByCampaign(
          customerId,
          campaignId,
          size,
          page,
          searchTerm,
          sortModel,
          filterItems,
        );
        // Ensure each ad group has a tags property and handle both string and object formats
        const adGroupsWithTags = (response.result || []).map((adGroup) => ({
          ...adGroup,
          tags: (adGroup as AdGroupListItem & { tags?: Array<string | Tag> }).tags || [],
        }));
        setAdGroups(adGroupsWithTags);
        setTotal(response.count || response.result.length);
        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
        console.error('Failed to fetch ad groups:', error);
      }
    },
    [campaignId, customerId],
  );

  const fetchTags = useCallback(async () => {
    try {
      const response = await searchTags(customerId, campaignId);
      setAvailableTags(response.result || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  }, [customerId, campaignId]);

  const fetchCampaignConfig = useCallback(async () => {
    const response = await listGoogleAdsCampaignsConfig({
      customerId,
      campaignId,
    });
    setCampaignConfig(response.result?.campaigns[0] || null);
  }, [customerId, campaignId]);

  useEffect(() => {
    fetchCampaignConfig();
  }, [fetchCampaignConfig]);

  useEffect(() => {
    debouncedUpdateFilterItems(filterModel.items);
    return () => {
      debouncedUpdateFilterItems.cancel();
    };
  }, [filterModel.items, debouncedUpdateFilterItems]);

  useEffect(() => {
    fetchAdGroups(pageSize, pageNum, search, sortModel, debouncedFilterItems);
  }, [fetchAdGroups, pageSize, pageNum, search, sortModel, debouncedFilterItems]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    setPageNum(model.page);
    setPageSize(model.pageSize);
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel({
      field: sortModel?.[0]?.field,
      sort: sortModel?.[0]?.sort,
    });
  };

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    setFilterModel(filterModel);
  }, []);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleModalClose = () => setIsModalOpen(false);
  const handleModalOpen = () => setIsModalOpen(true);
  const handleManageTagsModalClose = () => setIsManageTagsModalOpen(false);
  const handleManageTagsModalOpen = () => setIsManageTagsModalOpen(true);
  const handleTagsUpdated = () => {
    fetchTags();
    fetchAdGroups(pageSize, pageNum, search, sortModel, filterModel.items);
  };

  const processRowUpdate = useCallback(
    async (newRow: AdGroupListItemWithTags, oldRow: AdGroupListItemWithTags) => {
      if (newRow.tags !== oldRow.tags) {
        const newTagNames = newRow.tags.map((tag) => (typeof tag === 'string' ? tag : tag.tagName));
        const oldTagNames = oldRow.tags.map((tag) => (typeof tag === 'string' ? tag : tag.tagName));

        const removedTags = oldTagNames.filter((tag) => !newTagNames.includes(tag));

        const addedTags = newTagNames.filter((tag) => !oldTagNames.includes(tag));

        if (removedTags.length > 0) {
          for (const removedTagName of removedTags) {
            const removedTag = availableTags.find((tag) => tag.tagName === removedTagName);
            if (removedTag?.tagId) {
              await removeTagForAdGroups(customerId, campaignId, removedTag.tagId.toString(), [newRow.ad_group_id]);
            }
          }
        }

        if (addedTags.length > 0) {
          await updateAdGroupTags(customerId, campaignId, newRow.ad_group_id, newTagNames);
        }
      }
      return newRow;
    },
    [customerId, campaignId, availableTags],
  );

  return (
    <Paper sx={{ padding: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">
          Ad Groups for Campaign:{' '}
          <Link component={RouterLink} to={`/marketing/google-ads/customer/${customerId}/campaign/${campaignId}`}>
            {campaignId}
          </Link>{' '}
        </Typography>

        <TextField
          label="Search by Ad Group name or hotel name"
          variant="outlined"
          size="small"
          defaultValue={search}
          onChange={handleSearchChange}
          placeholder="Enter Ad Group Name or ID"
          sx={{ width: '500px' }}
        />
      </Box>

      <Box mb={2} display="flex" gap={2}>
        <Button variant="contained" onClick={handleModalOpen} disabled={selectionModel.length === 0}>
          Customize Rules
        </Button>
        <Button variant="contained" onClick={handleManageTagsModalOpen}>
          Manage Tags
        </Button>
      </Box>

      <DataGridPro
        rows={adGroups}
        columns={columns}
        loading={fetchingState === 'loading'}
        pagination
        paginationMode="server"
        filterMode="server"
        onFilterModelChange={onFilterChange}
        filterModel={filterModel}
        paginationModel={{ page: pageNum, pageSize }}
        pageSizeOptions={DEFAULT_PAGE_SIZES}
        rowCount={total}
        getRowId={(row) => row.ad_group_id}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{
          pagination: GridPagination,
          toolbar: () => {
            return (
              <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
              </GridToolbarContainer>
            );
          },
        }}
        autoHeight
        sortingMode="server"
        onSortModelChange={handleSortModelChange}
        checkboxSelection
        onRowSelectionModelChange={(newSelectionModel) => {
          setSelectionModel([...newSelectionModel]);
        }}
        rowSelectionModel={selectionModel}
        editMode="cell"
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(err) => console.error(err)}
        isCellEditable={(params) => params.field === 'tags'}
        disableRowSelectionOnClick
      />
      <Dialog open={isModalOpen} onClose={handleModalClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Ad Group Rules
          <Typography variant="body1">
            • You are setting multiple ad group rules at once.
            <br />• All Rule Definitions and Classifications on this page are inherited from the campaign.
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BidAutomationRules
            customerId={customerId}
            campaignId={campaignId}
            isAdGroupMultiSelect
            selectedAdGroupIds={selectionModel}
          />
        </DialogContent>
      </Dialog>
      <ManageTagsDialog
        open={isManageTagsModalOpen}
        onClose={handleManageTagsModalClose}
        customerId={customerId}
        campaignId={campaignId}
        onTagsUpdated={handleTagsUpdated}
      />
    </Paper>
  );
}

export default AdGroupListView;
